import React, { useState } from 'react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function ImageUpload({ customerId, onUploadSuccess }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const fileInputRef = React.useRef(null);

  const compressImage = async (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = (event) => {
        const img = new Image();
        img.src = event.target.result;
        img.onload = () => {
          const canvas = document.createElement('canvas');
          let width = img.width;
          let height = img.height;

          // Scale down if needed
          const maxDimension = 2000;
          if (width > maxDimension || height > maxDimension) {
            const ratio = Math.min(maxDimension / width, maxDimension / height);
            width *= ratio;
            height *= ratio;
          }

          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, width, height);

          // Compress with reducing quality until under 5MB
          let quality = 0.8;
          const tryCompress = () => {
            canvas.toBlob(
              (blob) => {
                if (blob.size <= 5 * 1024 * 1024 || quality <= 0.1) {
                  resolve(blob);
                } else {
                  quality -= 0.1;
                  tryCompress();
                }
              },
              'image/jpeg',
              quality
            );
          };
          tryCompress();
        };
        img.onerror = () => reject(new Error('Failed to load image'));
      };
      reader.onerror = () => reject(new Error('Failed to read file'));
    });
  };

  const handleFileSelect = async (file) => {
    if (!file) return;

    // Validate file type
    if (!['image/jpeg', 'image/png'].includes(file.type)) {
      setError('Only JPG and PNG files are supported');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      // Compress image if needed
      let uploadFile = file;
      if (file.size > 5 * 1024 * 1024) {
        setSuccess('⏳ Compressing image...');
        uploadFile = await compressImage(file);
      }

      const formData = new FormData();
      formData.append('image_file', uploadFile);

      const response = await fetch(`${BACKEND_URL}/api/customer/${customerId}/upload-image`, {
        method: 'POST',
        body: formData
      });

      const result = await response.json();

      if (response.ok) {
        setSuccess('✅ Image uploaded successfully!');
        if (onUploadSuccess) {
          onUploadSuccess(result);
        }
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      } else {
        setError(`❌ Error: ${result.detail || 'Failed to upload'}`);
      }
    } catch (err) {
      setError(`❌ Error: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      handleFileSelect(files[0]);
    }
  };

  const handleInputChange = (e) => {
    const files = e.target.files;
    if (files.length > 0) {
      handleFileSelect(files[0]);
    }
  };

  return (
    <div style={{ marginBottom: '20px' }}>
      <div
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        onClick={() => fileInputRef.current?.click()}
        style={{
          border: '2px dashed #1b7abf',
          borderRadius: '8px',
          padding: '30px',
          textAlign: 'center',
          cursor: 'pointer',
          backgroundColor: '#f9fafb',
          transition: 'all 0.2s'
        }}
        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#eff6ff'}
        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#f9fafb'}
      >
        <div style={{ fontSize: '24px', marginBottom: '8px' }}>📸</div>
        <p style={{ margin: '0 0 8px 0', color: '#1b7abf', fontWeight: 'bold' }}>
          Click to upload or drag & drop
        </p>
        <p style={{ margin: 0, color: '#666', fontSize: '14px' }}>
          JPG or PNG, max 5MB
        </p>
      </div>

      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/png"
        onChange={handleInputChange}
        style={{ display: 'none' }}
      />

      {error && (
        <div style={{
          marginTop: '12px',
          padding: '12px',
          backgroundColor: '#fee2e2',
          border: '1px solid #fca5a5',
          borderRadius: '6px',
          color: '#dc2626'
        }}>
          {error}
        </div>
      )}

      {success && (
        <div style={{
          marginTop: '12px',
          padding: '12px',
          backgroundColor: '#dcfce7',
          border: '1px solid #bbf7d0',
          borderRadius: '6px',
          color: '#166534'
        }}>
          {success}
        </div>
      )}

      {loading && (
        <div style={{
          marginTop: '12px',
          padding: '12px',
          backgroundColor: '#eff6ff',
          border: '1px solid #bfdbfe',
          borderRadius: '6px',
          color: '#1e40af',
          textAlign: 'center'
        }}>
          ⏳ Uploading...
        </div>
      )}
    </div>
  );
}
